import React, { useState, useRef, useEffect } from "react";
import { Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Button from "../command/Button/Button";
import "./auth.scss";
import { userClient } from "../../data/user";
import { productDetailsClient } from "../../data/product-details";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { formatCountryCodeWithPhoneNumber, formatPhoneNumber, isInternationalCountryCode } from "../../utils/utils";
import { useAuth } from "../../contexts/authContext";
import { jwtDecode } from "jwt-decode";
import { AUTH_CRED, deleteCookie, setAuthCredentials, getIdvBypass, getAuthCredentials } from "../../utils/auth-utils";

interface OtpFormModalProps {
  show: boolean;
  number?: any;
  onHide: () => void;
  onValueChange?: any;
  token?: string;
  username?: string;
  countryCode?: string;
  isInternational?: boolean;
  email?: string;
  byPassIDV?: boolean;
}

const OtpFormModal: React.FC<OtpFormModalProps> = ({
  onHide,
  onValueChange,
  byPassIDV,
  ...props
}) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [quote, setQuote] = useState<any>(null);
  const [idvBypass, setIdvBypass] = useState<boolean>(false);
  const { bypassIDV, maximumOrderValue, disableIDV } = getIdvBypass();
  const { quoteId } = getAuthCredentials();

  // Fetch quote information and check if IDV bypass should be enabled
  useEffect(() => {
    if (props.token) {
      productDetailsClient
        .getProductDetailsList({ quoteUuid: quoteId })
        .then((data: any) => {
          setQuote(data.quote);

          if (bypassIDV) {
            const idvBypass = Number(data.quote?.productRetailPrice) <= Number(maximumOrderValue ?? 0);
            setIdvBypass(idvBypass);
          }
        })
        .catch((error: any) => {
          toast.error(error?.message);
        });
    }
  }, [bypassIDV, maximumOrderValue, props.token]);

  const customerQuteUpdate = (customerid: string) => {
    // Check if we have a quote ID to update
    if (props.token) {
      const value = {
        customerQuoteUuid: quoteId,
        customerUuid: customerid,
      };
      productDetailsClient
        .updateCustomerQuote(value)
        .then((data: any) => {})
        .catch((error: any) => {
          toast.error(error?.message);
        });
    }
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (/^\d$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const resendCode = async () => {
    setOtp(Array(6).fill(""));
    const formattedNumber = formatCountryCodeWithPhoneNumber(props.countryCode || '', props.number);
    const email = props.email;
    const isInternational = props.isInternational;

    const number = {
      phoneNumber: formattedNumber,
      isInternational,
      email
    };
    try {
      userClient.sendOtp(number).then((res: any) => {
        if (res?.status === 'ok') {
          props.token = res.token;
          toast.success(`${t("re-sent-verification-code")}  ${props?.countryCode + props?.number}${isInternational ? ` and ${email}` : ''}`);
        }
      });
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const onSubmit = () => {
    if (props?.username) {
      const request = {
        otp: otp.filter((value: any) => value !== "").join(""),
        username: props?.username,
        userTypeId: byPassIDV ? 6 : 1,
      };
      userClient
        .verifyForgetPasswordOTP(request)
        .then((res) => {
          if (res.success) {
            toast.success("OTP Verification Successful");
            onHide();
            navigate("/resetpassword", { state: request });
          } else {
            toast.error(res?.error);
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
    } else {
      const formattedNumber = formatCountryCodeWithPhoneNumber(props.countryCode || '+44', props.number);

      const number = {
        otp: otp.filter((value: any) => value !== "").join(""),
        phoneNumber: formattedNumber,
        token: props.token,
      };
      userClient
        .verifyOTP(number)
        .then((res) => {
          if (res.status === "success") {
            toast.success("OTP Verification Successful");

            // Register the user with userTypeID 6
            const isInternational = isInternationalCountryCode(props.countryCode || '');
            
            const registerData = {
              email: props.email || '',
              password: '',
              phone: formattedNumber,
              customerType: 1,
              userTypeId: 6,
              countryCode: props.countryCode,
              countryNameCode: '',
              isInternational,
              customerQuoteUuid: quoteId,
            };
            
            userClient.registerCustomer(registerData)
              .then((registerRes) => {
                if (registerRes.success) {
                  const decodedToken: any = jwtDecode(registerRes.data.access_token);
                  const customerUuid = decodedToken?.uuid;
                  const userUuid = decodedToken?.userUuid;
                  const countryCode = decodedToken?.countryCode;
                  const isInternational = decodedToken?.isInternational;
                  deleteCookie(AUTH_CRED);
                  setAuthCredentials(
                    registerRes.data.access_token,
                    registerRes.data.refresh_token,
                    customerUuid,
                    userUuid,
                    null,
                    countryCode,
                    isInternational
                  );
                  login();
                  // customerQuteUpdate(userUuid);
                  toast.success("Registration successful");
                  
                  // Navigate based on IDV bypass rules
                  if (disableIDV || decodedToken?.isKYCVerified) {
                    navigate("/information-capture");
                  } else {
                    navigate("/verify-identity");
                  }
                } else {
                  toast.error(registerRes?.error || "Registration failed");
                  // onValueChange(true);
                }
              })
              .catch((registerError) => {
                toast.error(registerError?.message || "Registration failed");
                // onValueChange(true);
              });
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
    }
  };


  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Check if pasted data is numeric and has correct length
    if (!/^\d+$/.test(pastedData)) return;

    const pastedArray = pastedData.slice(0, 6).split("").map(char => char);
    setOtp(pastedArray.concat(Array(6 - pastedArray.length).fill("")));

    // Focus the last filled input or the next empty input
    const lastFilledIndex = Math.min(pastedArray.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  useEffect(() => {
    setOtp(Array(6).fill(""));
  }, [props.show]);
  return (
    <>
      <Modal
        {...props}
        aria-labelledby="otp-modal"
        centered
        onHide={onHide}
        className="otp-modal"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="otp-modal">{t("otp-verification")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="otp-modal-wrapper">
            <h4 className="otp-title">{t("verification-code-description")}:</h4>
            <h3 className="otp-mobile">
              {
                props?.username
                  ? props.username
                  : `${props.countryCode + props.number}${props.isInternational ? ` and ${props.email}` : ''}`
              }
            </h3>
            <Form className="otp-form d-flex justify-content-between">
              {otp.map((_, index) => (
                <Form.Control
                  key={index}
                  type="number"
                  maxLength={1}
                  value={otp[index]}
                  inputMode="numeric"
                  onChange={(e) =>
                    handleChange(
                      e as React.ChangeEvent<HTMLInputElement>,
                      index
                    )
                  }
                  onKeyDown={(e) =>
                    handleKeyDown(
                      e as React.KeyboardEvent<HTMLInputElement>,
                      index
                    )
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e)} // Add paste handler
                  ref={(el: HTMLInputElement) => {
                    inputRefs.current[index] = el as HTMLInputElement;
                  }}
                  className="otp-input-field"
                  required
                />
              ))}
            </Form>
            {otp.filter((value: any) => value !== "").join("").length < 6 &&
              !(
                otp.filter((value: any) => value !== "").join("").length === 0
              ) ? (
              <span className="text-danger otp-error">{t("otp-invalid")}</span>
            ) : (
              ""
            )}
            {props?.number && (
              <Link to={"#"} onClick={resendCode} className="auth-link">
                {t("resend-code")}
              </Link>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            btnText={t("continue")}
            onClick={onSubmit}
            disabled={
              otp.filter((value: any) => value !== "").join("").length < 6
            }
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OtpFormModal;
